import {
	TextField,
	Button,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Box,
	Typography,
	CircularProgress,
	Alert
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { CreateUserPayload } from '../models/user'
import { useUser } from '../hooks/useUser'
import useRole from '../hooks/useRole'

export const CreateUser = () => {
	const navigate = useNavigate()
	const { creating, error, createUser } = useUser()
	const { canCreateUser } = useRole()

	// Deshabilitar formulario si no tiene permisos
	const isFormDisabled = !canCreateUser()

	const {
		control,
		handleSubmit,
		formState: { isSubmitting }
	} = useForm<CreateUserPayload & { photo: FileList }>()

	// Helper para convertir File a base64
	const toBase64 = (file: File): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.readAsDataURL(file)
			reader.onload = () => {
				const dataUrl = reader.result as string
				resolve(dataUrl.split(',')[1])
			}
			reader.onerror = (error) => reject(error)
		})

	const onSubmit = async (data: CreateUserPayload & { photo: FileList }) => {
		const payload: CreateUserPayload = {
			firstname: data.firstname,
			lastname: data.lastname,
			email: data.email,
			role: data.role,
			position: data.position
			// No enviamos password, se generará en el backend
		}

		if (data.photo && data.photo.length > 0) {
			const file = data.photo[0]
			const ext = file.name.split('.').pop()?.toLowerCase()
			const base64 = await toBase64(file)
			payload.image = { ext: ext ?? '', data: base64 }
		}

		if (await createUser(payload)) {
			alert('Usuario creado correctamente. Se han enviado las credenciales de acceso al correo electrónico.')
			navigate('/home')
		}
	}

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				maxWidth: 750,
				margin: 'auto',
				p: 2,
				boxShadow: 3,
				borderRadius: 2,
				bgcolor: 'background.paper',
				mt: 10
			}}
			component="form"
			onSubmit={handleSubmit(onSubmit)}
		>
			{!canCreateUser() && (
				<Alert severity="error" sx={{ mb: 2 }}>
					No tienes permisos para crear usuarios. Solo los administradores pueden realizar esta acción.
				</Alert>
			)}
			<Typography variant="h5" component="h1" align="center">
				Crear Usuario
			</Typography>

			{error && <Alert severity="error">{error}</Alert>}

			<Controller
				name="firstname"
				control={control}
				defaultValue=""
				rules={{ required: 'Nombres es obligatorio' }}
				render={({ field, fieldState }) => (
					<TextField
						{...field}
						label="Nombres"
						variant="outlined"
						fullWidth
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						disabled={isFormDisabled}
					/>
				)}
			/>

			<Controller
				name="lastname"
				control={control}
				defaultValue=""
				rules={{ required: 'Apellidos es obligatorio' }}
				render={({ field, fieldState }) => (
					<TextField
						{...field}
						label="Apellidos"
						variant="outlined"
						fullWidth
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						disabled={isFormDisabled}
					/>
				)}
			/>

			<Controller
				name="email"
				control={control}
				defaultValue=""
				rules={{
					required: 'Correo electrónico es obligatorio',
					pattern: { value: /\S+@\S+\.\S+/, message: 'Correo no válido' }
				}}
				render={({ field, fieldState }) => (
					<TextField
						{...field}
						label="Correo Electrónico"
						type="email"
						variant="outlined"
						fullWidth
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						disabled={isFormDisabled}
					/>
				)}
			/>

			<Controller
				name="position"
				control={control}
				defaultValue=""
				rules={{ required: 'Posición es obligatoria' }}
				render={({ field, fieldState }) => (
					<TextField
						{...field}
						label="Posición"
						variant="outlined"
						fullWidth
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						disabled={isFormDisabled}
					/>
				)}
			/>

			<Controller
				name="role"
				control={control}
				defaultValue="USER_ROLE"
				render={({ field }) => (
					<FormControl fullWidth>
						<InputLabel id="role-label">Role</InputLabel>
						<Select {...field} labelId="role-label" label="Role" disabled={isFormDisabled}>
							<MenuItem value="ADMIN_ROLE">Administrador</MenuItem>
							<MenuItem value="AUDIT_ROLE">Auditor</MenuItem>
							<MenuItem value="USER_ROLE">Usuario</MenuItem>
						</Select>
					</FormControl>
				)}
			/>

			<Controller
				name="photo"
				control={control}
				defaultValue={undefined}
				render={({ field }) => (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<TextField
							label="Fotografía"
							variant="outlined"
							fullWidth
							value={field.value && field.value.length ? (field.value[0] as File).name : ''}
							disabled
						/>
						<Button variant="contained" component="label">
							Subir Archivo
							<input
								hidden
								accept="image/*"
								type="file"
								onChange={(e) => field.onChange(e.target.files)}
							/>
						</Button>
					</Box>
				)}
			/>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
				Se generará una contraseña automáticamente y será enviada al correo electrónico del usuario.
			</Typography>
			
			<Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
				Tamaño máximo para imágenes: 100MB
			</Typography>

			<Button
				type="submit"
				variant="contained"
				color="primary"
				size="large"
				fullWidth
				disabled={isSubmitting || creating || !canCreateUser()}
			>
				{creating ? <CircularProgress size={24} /> : 'Crear Usuario'}
			</Button>
		</Box>
	)
}
